import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { PageHeading, Pill, NtIcon, STATUS_COLORS, PRIORITY_COLORS } from '../components/NotionUI';
import { getTicketDashboard, errorMessage } from '../services/api';
import { useLanguage } from '../i18n/LanguageContext';
import { fmtNum, fmtRelativeDate } from '../utils/format';
import SlaPanel from '../components/SlaPanel';
import CsatPanel from '../components/CsatPanel';

const STATUS_ORDER = ['open', 'in_progress', 'waiting_customer', 'resolved', 'closed'];
const PRIORITY_ORDER = ['urgent', 'high', 'medium', 'low'];

/** Andamento: barre affiancate ticket creati / risolti per giorno. */
function TrendChart({ data, t }) {
  const W = 620;
  const H = 190;
  const pad = { top: 10, right: 8, bottom: 24, left: 28 };
  const max = Math.max(4, ...data.map((d) => Math.max(d.created, d.resolved)));
  const step = Math.ceil(max / 4);
  const top = step * 4;
  const innerW = W - pad.left - pad.right;
  const innerH = H - pad.top - pad.bottom;
  const slot = innerW / data.length;
  const barW = Math.min(14, slot / 2 - 2);
  const y = (v) => pad.top + innerH - (v / top) * innerH;

  return (
    <svg className="db-trend" viewBox={`0 0 ${W} ${H}`} role="img" aria-label={t('dashboard.trendTitle', { days: data.length })}>
      {[0, 1, 2, 3, 4].map((i) => (
        <g key={i}>
          <line x1={pad.left} x2={W - pad.right} y1={y(i * step)} y2={y(i * step)} className="db-grid" />
          <text x={pad.left - 6} y={y(i * step) + 4} textAnchor="end" className="db-axis">{i * step}</text>
        </g>
      ))}
      {data.map((d, i) => {
        const cx = pad.left + slot * i + slot / 2;
        const day = Number(d.date.slice(8, 10));
        return (
          <g key={d.date}>
            <rect x={cx - barW - 1} y={y(d.created)} width={barW} height={Math.max(0, pad.top + innerH - y(d.created))} rx="3" className="db-bar-created">
              <title>{`${d.date}: ${t('dashboard.legendCreated')} ${d.created}`}</title>
            </rect>
            <rect x={cx + 1} y={y(d.resolved)} width={barW} height={Math.max(0, pad.top + innerH - y(d.resolved))} rx="3" className="db-bar-resolved">
              <title>{`${d.date}: ${t('dashboard.legendResolved')} ${d.resolved}`}</title>
            </rect>
            {(i % 2 === 0 || data.length < 9) && <text x={cx} y={H - 6} textAnchor="middle" className="db-axis">{day}</text>}
          </g>
        );
      })}
    </svg>
  );
}

/** Ciambella con la ripartizione per stato. */
function StatusDonut({ byStatus, t }) {
  const total = STATUS_ORDER.reduce((sum, s) => sum + (byStatus[s] || 0), 0);
  const R = 52;
  const C = 2 * Math.PI * R;
  let offset = 0;

  return (
    <div className="db-donut-wrap">
      <svg className="db-donut" viewBox="0 0 140 140" role="img" aria-label={t('dashboard.statusTitle')}>
        <circle cx="70" cy="70" r={R} className="db-donut-track" />
        {total > 0 && STATUS_ORDER.map((s) => {
          const value = byStatus[s] || 0;
          if (!value) return null;
          const len = (value / total) * C;
          const el = (
            <circle
              key={s}
              cx="70"
              cy="70"
              r={R}
              fill="none"
              stroke={STATUS_COLORS[s]}
              strokeWidth="16"
              strokeDasharray={`${Math.max(0, len - 2)} ${C - Math.max(0, len - 2)}`}
              strokeDashoffset={-offset}
              transform="rotate(-90 70 70)"
            >
              <title>{`${t(`tickets.status_${s}`)}: ${value}`}</title>
            </circle>
          );
          offset += len;
          return el;
        })}
        <text x="70" y="68" textAnchor="middle" className="db-donut-total">{fmtNum(total)}</text>
        <text x="70" y="86" textAnchor="middle" className="db-donut-label">{t('dashboard.statusTotal')}</text>
      </svg>
      <ul className="db-legend">
        {STATUS_ORDER.map((s) => (
          <li key={s}>
            <span className="db-dot" style={{ background: STATUS_COLORS[s] }} />
            <span className="db-legend-name">{t(`tickets.status_${s}`)}</span>
            <strong>{byStatus[s] || 0}</strong>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Elenco di barre orizzontali (coda / priorità / operatori). */
function HBars({ rows, empty }) {
  const max = Math.max(1, ...rows.map((r) => r.value));
  if (rows.length === 0) return <p className="db-empty">{empty}</p>;
  return (
    <ul className="db-hbars">
      {rows.map((r) => (
        <li key={r.key}>
          <div className="db-hbar-head">
            <span className="db-hbar-name">{r.label}</span>
            <strong>{r.value}</strong>
            {r.note && <span className="db-hbar-note">{r.note}</span>}
          </div>
          <div className="db-hbar-track">
            <span className="db-hbar-fill" style={{ width: `${(r.value / max) * 100}%`, background: r.color }} />
          </div>
        </li>
      ))}
    </ul>
  );
}

function TicketRow({ ticket, onOpen, t }) {
  return (
    <button type="button" className="db-ticket" onClick={() => onOpen(ticket.id)}>
      <span className="db-ticket-bar" style={{ background: ticket.project_color || 'var(--border)' }} aria-hidden="true" />
      <span className="db-ticket-main">
        <span className="db-ticket-subject"><span className="db-ticket-id">#{ticket.id}</span> {ticket.subject}</span>
        <span className="db-ticket-meta">
          {ticket.project_name && <span>{ticket.project_name}</span>}
          {ticket.customer && <span>{ticket.customer}</span>}
          <span>{ticket.assignee || t('dashboard.unassigned')}</span>
          <span>{fmtRelativeDate(ticket.created_at)}</span>
        </span>
      </span>
      <span className="db-ticket-tags">
        {ticket.is_overdue && <span className="db-overdue">{t('dashboard.overdueLabel')}</span>}
        <Pill color={PRIORITY_COLORS[ticket.priority]}>{t(`tickets.priority_${ticket.priority}`)}</Pill>
        <Pill dot color={STATUS_COLORS[ticket.status]}>{t(`tickets.status_${ticket.status}`)}</Pill>
      </span>
    </button>
  );
}

function Kpi({ icon, tone, value, label, hint }) {
  return (
    <div className={`cu-stat db-kpi${tone ? ` db-kpi-${tone}` : ''}`}>
      <span className="cu-stat-icon"><NtIcon name={icon} size={18} /></span>
      <span className="cu-stat-value">{value}</span>
      <span className="cu-stat-label">{label}</span>
      {hint && <span className="db-kpi-hint">{hint}</span>}
    </div>
  );
}

/** Dashboard dei ticket: indicatori, andamento, ripartizioni e ticket da guardare. */
function TicketDashboard({ user, onLogout }) {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getTicketDashboard()
      .then((res) => setData(res.dashboard || null))
      .catch((err) => setError(errorMessage(err, t('dashboard.loadError'))))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fullName = [user?.name, user?.surname].filter(Boolean).join(' ') || user?.email || '';
  const openTicket = (id) => navigate(`/tickets/${id}`, { state: { from: '/dashboard' } });
  const d = data && data.has_tickets ? data : null;

  const trendCreated = d ? d.trend.reduce((sum, x) => sum + x.created, 0) : 0;
  const trendResolved = d ? d.trend.reduce((sum, x) => sum + x.resolved, 0) : 0;

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="nt-page">
        <PageHeading icon="layout" title={t('dashboard.title')} subtitle={t('dashboard.subtitle', { name: fullName })} />

        {error && <div className="error-message">{error}</div>}

        {loading ? (
          <div className="loading">{t('common.loading')}</div>
        ) : !d ? (
          !error && <p className="nt-empty">{t('dashboard.noAccess')}</p>
        ) : (
          <>
            <div className="db-kpis">
              <Kpi icon="ticket" value={fmtNum(d.kpi.open)} label={t('dashboard.kpiOpen')} hint={d.kpi.created_today > 0 ? t('dashboard.createdToday', { count: d.kpi.created_today }) : null} />
              <Kpi icon="users" tone="orange" value={fmtNum(d.kpi.unassigned)} label={t('dashboard.kpiUnassigned')} />
              <Kpi icon="clock" tone={d.kpi.overdue > 0 ? 'red' : 'green'} value={fmtNum(d.kpi.overdue)} label={t('dashboard.kpiOverdue')} />
              <Kpi icon="status" tone="purple" value={fmtNum(d.kpi.waiting_customer)} label={t('dashboard.kpiWaiting')} />
              <Kpi
                icon="priority"
                tone="green"
                value={fmtNum(d.kpi.resolved_week)}
                label={t('dashboard.kpiResolved')}
                hint={d.kpi.avg_resolution_hours !== null ? t('dashboard.avgResolution', { hours: d.kpi.avg_resolution_hours }) : null}
              />
            </div>

            <div className="db-row db-row-2">
              <section className="db-card">
                <header className="db-card-head">
                  <h3>{t('dashboard.trendTitle', { days: d.trend.length })}</h3>
                  <span className="db-trend-legend">
                    <span><i className="db-key db-key-created" /> {t('dashboard.legendCreated')} <strong>{trendCreated}</strong></span>
                    <span><i className="db-key db-key-resolved" /> {t('dashboard.legendResolved')} <strong>{trendResolved}</strong></span>
                  </span>
                </header>
                <TrendChart data={d.trend} t={t} />
              </section>

              <section className="db-card">
                <header className="db-card-head"><h3>{t('dashboard.statusTitle')}</h3></header>
                <StatusDonut byStatus={d.by_status} t={t} />
              </section>
            </div>

            <div className="db-row db-row-3">
              <section className="db-card">
                <header className="db-card-head"><h3>{t('dashboard.queueTitle')}</h3></header>
                <HBars
                  empty={t('dashboard.noOpen')}
                  rows={d.by_project.map((p) => ({ key: p.id, label: p.name, value: p.open, color: p.color }))}
                />
              </section>
              <section className="db-card">
                <header className="db-card-head"><h3>{t('dashboard.priorityTitle')}</h3></header>
                <HBars
                  empty={t('dashboard.noOpen')}
                  rows={PRIORITY_ORDER.filter((pr) => d.by_priority[pr] > 0).map((pr) => ({ key: pr, label: t(`tickets.priority_${pr}`), value: d.by_priority[pr], color: PRIORITY_COLORS[pr] }))}
                />
              </section>
              {d.scope === 'all' ? (
                <section className="db-card">
                  <header className="db-card-head"><h3>{t('dashboard.workloadTitle')}</h3></header>
                  <HBars
                    empty={t('dashboard.noAssigned')}
                    rows={d.workload.map((w) => ({ key: w.id, label: w.name, value: w.open, note: w.collaborating > 0 ? t('dashboard.collaborating', { count: w.collaborating }) : null, color: 'var(--primary)' }))}
                  />
                </section>
              ) : (
                <section className="db-card">
                  <header className="db-card-head"><h3>{t('dashboard.mineTitle')}</h3></header>
                  <div className="db-big">{fmtNum(d.kpi.mine)}</div>
                  <p className="db-empty">{t('dashboard.scopeOwn')}</p>
                </section>
              )}
            </div>

            <SlaPanel />
            <CsatPanel />

            <div className="db-row db-row-2b">
              <section className="db-card">
                <header className="db-card-head">
                  <h3>{t('dashboard.attentionTitle')}</h3>
                  {d.attention.length > 0 && <span className="db-count">{d.attention.length}</span>}
                </header>
                {d.attention.length === 0 ? (
                  <p className="db-empty db-ok"><NtIcon name="priority" size={16} /> {t('dashboard.noAttention')}</p>
                ) : (
                  <div className="db-tickets">
                    {d.attention.map((tk) => <TicketRow key={tk.id} ticket={tk} onOpen={openTicket} t={t} />)}
                  </div>
                )}
              </section>

              <section className="db-card">
                <header className="db-card-head">
                  <h3>{t('dashboard.recentTitle')}</h3>
                  <button type="button" className="nt-ghost-btn" onClick={() => navigate('/tickets')}>{t('dashboard.allTickets')} →</button>
                </header>
                {d.recent.length === 0 ? (
                  <p className="db-empty">{t('dashboard.noTickets')}</p>
                ) : (
                  <div className="db-tickets">
                    {d.recent.map((tk) => <TicketRow key={tk.id} ticket={tk} onOpen={openTicket} t={t} />)}
                  </div>
                )}
              </section>
            </div>

            {d.projects && (
              <button type="button" className="db-strip" onClick={() => navigate('/projects')}>
                <span className="cu-stat-icon"><NtIcon name="briefcase" size={18} /></span>
                <span className="db-strip-text">
                  <strong>{t('dashboard.projectsStrip', { projects: d.projects.projects, tasks: d.projects.tasks })}</strong>
                  <span>{t('dashboard.projectsHint')}</span>
                </span>
                <span aria-hidden="true">→</span>
              </button>
            )}
          </>
        )}
      </div>
    </Layout>
  );
}

export default TicketDashboard;
